import type {ImageTilesDescriptor} from "@cm/utils"
import {isPromise} from "@cm/utils"
import type {Three as THREE} from "#three-texture-streaming/three-exports"
import {ThreeTsl as TSL} from "#three-texture-streaming/three-exports"
import type {CmLogger} from "@cm/log"
import {TEXTURE_TILES_MAX_IDS, TRACE_TILED_TEXTURES, USE_DEBUG_TILE_IMAGE} from "#three-texture-streaming/texture-tile-constants"
import type {ExplicitTextureStreamingMode} from "#three-texture-streaming/types"
import type {TextureTileId} from "#three-texture-streaming/texture-tile-id"
import {decodeTextureTileId, encodeTextureTileId} from "#three-texture-streaming/texture-tile-id"
import type {ThreeTiledTexture} from "#three-texture-streaming/three-tiled-texture"
import type {RequestedTilesOptions, ThreeTiledTextureCallback} from "#three-texture-streaming/three-tiled-texture-callback"
import {ThreeTiledTextureInstance} from "#three-texture-streaming/three-tiled-texture-instance"
import {ThreeTextureTileAtlasManager} from "#three-texture-streaming/three-texture-tile-atlas-manager"
import {ThreeViewportTextureTileUpdater} from "#three-texture-streaming/three-viewport-texture-tile-updater"
import {ThreeViewportTextureTileUpdaterMaterialCache} from "#three-texture-streaming/three-viewport-texture-tile-updater-material-cache"
import type {AbstractTileLoader} from "#three-texture-streaming/abstract-tile-loader"
import type {ThreeTextureTilesManagerCallback} from "#three-texture-streaming/three-texture-tiles-manager-callback"
import type {TileRequest, TileResponse} from "#three-texture-streaming/tile-request-response"

const TRACE = TRACE_TILED_TEXTURES
const PRIORITY_INITIAL_TILE = 1_000_000

type TileWriteInfo = {
    textureTileId: TextureTileId
    priority: number
    shouldLockTile: boolean
    abortController: AbortController
    complete: () => void
    abort: () => void
    downPrioritize: () => void
}

type TileWriteRequest = {
    tileWriteInfo: TileWriteInfo
    lastRequestedFrame: number
    updatePriority: (newPriority: number) => void
}

export class ThreeTextureTilesManager<TResponseMap extends {TileResponse: TileResponse} = {TileResponse: TileResponse}> {
    constructor(
        private readonly callback: ThreeTextureTilesManagerCallback,
        private readonly textureTileLoader: AbstractTileLoader<TileRequest, TResponseMap>,
        private readonly logger: CmLogger,
    ) {
        this.availableTiledTextureIds = new Set<number>()
        for (let i = 1; i < TEXTURE_TILES_MAX_IDS; i++) {
            this.availableTiledTextureIds.add(i)
        }
        this.tiledTextureCallback = {
            disposeTiledTexture: (tiledTexture) => this.disposeTiledTexture(tiledTexture),
            requestTiles: (tiles, options) => this.requestTiles(tiles, options),
            getTileRequestShaderMaterials: (materialInfo, frameIndex) => {
                const renderer = this.callback.getRenderer()
                if (!renderer) {
                    throw new Error("ThreeTextureTilesManager: no renderer available")
                }
                return this.tileUpdaterMaterialCache.getTileRequestShaderMaterials(
                    materialInfo,
                    frameIndex,
                    renderer,
                    this.currentStreamingMode,
                    this.currentMaxPixelsPerTexel,
                )
            },
            getMeshMaterialInfo: (mesh) => this.callback.getMeshMaterialInfo(mesh),
            getAtlasManager: () => this.atlasManager,
            getDummyUvNode: () => this.dummyUvNode,
        }
    }

    dispose(): void {
        this.tileUpdaterMaterialCacheInstance?.dispose()
        this.tileUpdaterMaterialCacheInstance = undefined
        this.atlasManagerInstance?.dispose()
        this.atlasManagerInstance = undefined
    }

    setTextureFiltering(linear: boolean): void {
        this.textureFiltering = linear
        this.atlasManagerInstance?.setFiltering(linear)
    }

    setStreamingMode(streamingMode: ExplicitTextureStreamingMode): void {
        this.currentStreamingMode = streamingMode
        this.atlasManagerInstance?.setStreamingMode(streamingMode)
    }

    get streamingMode(): ExplicitTextureStreamingMode {
        return this.currentStreamingMode
    }

    setMaxPixelsPerTexel(maxPixelsPerTexel: number): void {
        this.currentMaxPixelsPerTexel = maxPixelsPerTexel
        this.atlasManagerInstance?.setMaxPixelsPerTexel(maxPixelsPerTexel)
    }

    get maxPixelsPerTexel(): number {
        return this.currentMaxPixelsPerTexel
    }

    get tileUpdaterMaterialCache(): ThreeViewportTextureTileUpdaterMaterialCache {
        if (!this.tileUpdaterMaterialCacheInstance) {
            throw new Error("ThreeTextureTilesManager: tileUpdaterMaterialCache not available (disposed?)")
        }
        return this.tileUpdaterMaterialCacheInstance
    }

    async createViewportTextureTileUpdater(): Promise<ThreeViewportTextureTileUpdater> {
        const renderer = await this.callback.getRendererAsync()
        return new ThreeViewportTextureTileUpdater(this.tiledTextureCallback, renderer)
    }

    async createTiledTexture(
        imageTilesDescriptor: ImageTilesDescriptor,
        isSrgb: boolean,
        uvNode: THREE.Node<"vec2"> | THREE.Node<"vec3"> | undefined,
        maxTextureResolution?: number,
    ): Promise<ThreeTiledTextureInstance> {
        // awaited up front (before the registry lookup below) so that the lookup and the first registration write
        // stay adjacent with no await between them - otherwise concurrent calls for the same tileDownloadUrlPrefix
        // could both see no existing entry and each register their own ThreeTiledTexture for the same URL
        await this.callback.getRendererAsync() // make sure the renderer has been initialized
        let tiledTexture = this.tiledTextureByIndexDownloadUrl.get(imageTilesDescriptor.tileDownloadUrlPrefix)
        if (!tiledTexture) {
            if (TRACE) {
                this.logger.log("ThreeTextureTilesManager: loading tiledTexture", tiledTexture)
            }
            const id = this.fetchNextFreeTiledTextureId()
            try {
                tiledTexture = this.callback.createTiledTexture(this.tiledTextureCallback, id, imageTilesDescriptor, isSrgb)
                this.tiledTextureByIndexDownloadUrl.set(imageTilesDescriptor.tileDownloadUrlPrefix, tiledTexture)
                tiledTexture = await tiledTexture
                this.tiledTextureByIndexDownloadUrl.set(imageTilesDescriptor.tileDownloadUrlPrefix, tiledTexture)
                this.tiledTextureById.set(id, tiledTexture)
                this.textureTileIdsByTiledTextureId.set(id, new Set<TextureTileId>())
                this.buildDependentTilesLookup(tiledTexture)
                await this.updateCoarsestTile(tiledTexture)
            } catch (error) {
                this.tiledTextureByIndexDownloadUrl.delete(imageTilesDescriptor.tileDownloadUrlPrefix)
                this.tiledTextureById.delete(id)
                this.textureTileIdsByTiledTextureId.delete(id)
                if (tiledTexture && !isPromise(tiledTexture)) {
                    this.removeDependentTilesLookup(tiledTexture)
                }
                this.logger.error("ThreeTextureTilesManager: failed to load tiledTexture", error)
                throw error
            }
        } else if (isPromise(tiledTexture)) {
            if (TRACE) {
                this.logger.log("ThreeTextureTilesManager: awaiting tiledTexture to be loaded for instantiation", tiledTexture)
            }
            tiledTexture = await tiledTexture
            tiledTexture.addRef()
        } else {
            if (TRACE) {
                this.logger.log("ThreeTextureTilesManager: instantiating existing tiledTexture", tiledTexture)
            }
            tiledTexture.addRef()
        }
        const minMipLevel = maxTextureResolution
            ? Math.max(
                  0,
                  tiledTexture.numMipLevels -
                      Math.ceil(Math.log2(maxTextureResolution / Math.max(tiledTexture.tileSize.width, tiledTexture.tileSize.height)) + 1),
              )
            : 0
        const tiledTextureInstance = new ThreeTiledTextureInstance(tiledTexture, uvNode ?? this.dummyUvNode, minMipLevel)
        tiledTexture.release()
        return tiledTextureInstance
    }

    private getTiledTexture(id: number): ThreeTiledTexture {
        const tiledTexture = this.tiledTextureById.get(id)
        if (!tiledTexture) {
            throw new Error(`ThreeTextureTilesManager: unknown tiledTexture id ${id}`)
        }
        return tiledTexture
    }

    private disposeTiledTexture(tiledTexture: ThreeTiledTexture): void {
        const tileDownloadUrlPrefix = tiledTexture.imageTilesDescriptor.tileDownloadUrlPrefix
        if (!this.tiledTextureByIndexDownloadUrl.has(tileDownloadUrlPrefix)) {
            throw new Error("ThreeTextureTilesManager: trying to dispose unknown tiledTexture")
        }
        const textureTileIds = this.textureTileIdsByTiledTextureId.get(tiledTexture.id)
        if (!textureTileIds) {
            throw new Error("ThreeTextureTilesManager: inconsistent state - no tiles tracked for tiledTexture")
        }
        Array.from(textureTileIds).forEach((textureTileId) => this.removeTile(textureTileId, true, false))
        if (textureTileIds.size > 0) {
            throw new Error("ThreeTextureTilesManager: inconsistent state - some tiles still tracked for tiledTexture on dispose")
        }
        this.textureTileIdsByTiledTextureId.delete(tiledTexture.id)
        this.tiledTextureByIndexDownloadUrl.delete(tileDownloadUrlPrefix)
        this.tiledTextureById.delete(tiledTexture.id)
        this.removeDependentTilesLookup(tiledTexture)
        this.availableTiledTextureIds.add(tiledTexture.id)
        if (TRACE) {
            this.logger.log("ThreeTextureTilesManager: disposed tiledTexture", tiledTexture)
        }
    }

    private buildDependentTilesLookup(tiledTexture: ThreeTiledTexture): void {
        let numTiles = tiledTexture.numTiles
        for (let mipLevel = 0; mipLevel < tiledTexture.numMipLevels; mipLevel++) {
            for (let y = 0; y < numTiles.y; y++) {
                for (let x = 0; x < numTiles.x; x++) {
                    const tileIndex = {x, y}
                    const textureTileId = encodeTextureTileId(tiledTexture.id, mipLevel, tileIndex)
                    const dependentTextureTileIds: TextureTileId[] = []
                    // add 8 neighboring tiles from same mip level
                    for (let offsetY = -1; offsetY <= 1; offsetY++) {
                        for (let offsetX = -1; offsetX <= 1; offsetX++) {
                            if (offsetX === 0 && offsetY === 0) {
                                continue
                            }
                            const neighborX = x + offsetX
                            const neighborY = y + offsetY
                            if (neighborX >= 0 && neighborX < numTiles.x && neighborY >= 0 && neighborY < numTiles.y) {
                                const neighborTileIndex = {x: neighborX, y: neighborY}
                                const neighborTextureTileId = encodeTextureTileId(tiledTexture.id, mipLevel, neighborTileIndex)
                                dependentTextureTileIds.push(neighborTextureTileId)
                            }
                        }
                    }
                    // add coarser mip level tiles (excluding the coarsest level N-1, which must
                    // never be tracked/requested as a standalone tile - see updateTile())
                    for (let coarserMipLevel = mipLevel + 1; coarserMipLevel < tiledTexture.numMipLevels - 1; coarserMipLevel++) {
                        const coarserTileIndex = {
                            x: x >> (coarserMipLevel - mipLevel),
                            y: y >> (coarserMipLevel - mipLevel),
                        }
                        const coarserTextureTileId = encodeTextureTileId(tiledTexture.id, coarserMipLevel, coarserTileIndex)
                        dependentTextureTileIds.push(coarserTextureTileId)
                    }
                    this.dependentTextureTileIdsByTextureTileId.set(textureTileId, dependentTextureTileIds)
                }
            }
            numTiles = {
                x: Math.ceil(numTiles.x / 2),
                y: Math.ceil(numTiles.y / 2),
            }
        }
    }

    private removeDependentTilesLookup(tiledTexture: ThreeTiledTexture): void {
        let numTiles = tiledTexture.numTiles
        for (let mipLevel = 0; mipLevel < tiledTexture.numMipLevels; mipLevel++) {
            for (let y = 0; y < numTiles.y; y++) {
                for (let x = 0; x < numTiles.x; x++) {
                    const tileIndex = {x, y}
                    const textureTileId = encodeTextureTileId(tiledTexture.id, mipLevel, tileIndex)
                    this.dependentTextureTileIdsByTextureTileId.delete(textureTileId)
                }
            }
            numTiles = {
                x: Math.ceil(numTiles.x / 2),
                y: Math.ceil(numTiles.y / 2),
            }
        }
    }

    private removeTile(textureTileId: TextureTileId, removeFromAtlas: boolean, updateTileLookup: boolean): void {
        const {tiledTextureId, mipLevel, tileIndex} = decodeTextureTileId(textureTileId)
        const writeRequest = this.tileWriteRequestByTextureTileIds.get(textureTileId)
        if (writeRequest) {
            writeRequest.tileWriteInfo.abort()
        } else {
            const textureTileIds = this.textureTileIdsByTiledTextureId.get(tiledTextureId)
            if (!textureTileIds || !textureTileIds.has(textureTileId)) {
                throw new Error("ThreeTextureTilesManager: inconsistent state - evicted tile not tracked")
            }
            textureTileIds.delete(textureTileId)
            if (removeFromAtlas) {
                // if there's no atlas instance, it (and its tiles) has already been disposed - nothing to evict
                this.atlasManagerInstance?.removeTile(textureTileId)
            }
        }
        if (updateTileLookup) {
            const tiledTexture = this.getTiledTexture(tiledTextureId)
            tiledTexture.setTileLookup(mipLevel, tileIndex, null)
        }
    }

    private requestTiles(textureTileIds: Iterable<TextureTileId>, options?: RequestedTilesOptions): void {
        const loadProgressively = options?.loadProgressively ?? true
        if (!loadProgressively) {
            throw new Error("ThreeTextureTilesManager: non-progressive tile loading not implemented")
        }
        const newRequestedTiles = new Set<TextureTileId>()
        for (const textureTileId of textureTileIds) {
            const dependentTextureTileIds = this.dependentTextureTileIdsByTextureTileId.get(textureTileId)
            if (!dependentTextureTileIds) {
                continue // can happen for OOB tile requests due to limited shader precision or stale texture IDs
            }
            newRequestedTiles.add(textureTileId)
            for (const dependentTextureTileId of dependentTextureTileIds) {
                newRequestedTiles.add(dependentTextureTileId)
            }
        }

        this.currentFrame++

        for (const requestedTile of newRequestedTiles) {
            this.updateTile(requestedTile)
        }

        this.handleLegacyTileWriteRequests()

        if (TRACE && this.logger.isEnabled("trace")) {
            this.logger.trace(
                `ThreeTextureTilesManager: requested tiles`,
                [...textureTileIds].map((id) => decodeTextureTileId(id)),
            )
        }
    }

    private updateTile(textureTileId: TextureTileId): void {
        if (this.atlasManager.hasTile(textureTileId)) {
            this.atlasManager.markTileAsUsed(textureTileId, this.currentFrame)
            return
        }
        const {tiledTextureId, mipLevel} = decodeTextureTileId(textureTileId)
        const numMipLevels = this.tiledTextureById.get(tiledTextureId)?.numMipLevels ?? 0
        // N-1 tiles must never be requested standalone: their atlas mip1 is never
        // written (no coarserTileImage), so sampling with effectiveMipFrac > 0 would
        // produce garbage. N-1 content is reached via atlas mip1 of N-2 tile slots.
        if (mipLevel > numMipLevels - 2) {
            this.logger.error(
                `ThreeTextureTilesManager: unexpected standalone request for coarsest mip level ${mipLevel} (numMipLevels=${numMipLevels}); shader clamping may be missing`,
            )
            return
        }
        const shouldLockTile = mipLevel === numMipLevels - 2 // lock the coarsest tiles
        void this.requestWriteTile(textureTileId, mipLevel, shouldLockTile, true)
    }

    private requestWriteTile(textureTileId: TextureTileId, priority: number, shouldLockTile: boolean, asBackgroundTask: boolean): Promise<void> {
        const existingTileWriteRequest = this.tileWriteRequestByTextureTileIds.get(textureTileId)
        if (existingTileWriteRequest) {
            existingTileWriteRequest.lastRequestedFrame = this.currentFrame
            existingTileWriteRequest.updatePriority(priority)
            return Promise.resolve()
        }

        const {tiledTextureId} = decodeTextureTileId(textureTileId)
        const textureTileIds = this.textureTileIdsByTiledTextureId.get(tiledTextureId)
        if (!textureTileIds) {
            throw new Error("ThreeTextureTilesManager: inconsistent state - no tiles tracked for tiledTexture")
        }
        textureTileIds.add(textureTileId)

        const removeWriteRequest = (aborted: boolean) => {
            if (!this.tileWriteRequestByTextureTileIds.has(textureTileId)) {
                throw new Error("ThreeTextureTilesManager: inconsistent state - write request not tracked")
            }
            this.tileWriteRequestByTextureTileIds.delete(textureTileId)
            this.numTilesLoading--
            this.callback.onLoadingCountChanged(this.numTilesLoading)

            if (aborted) {
                tileWriteInfo.abortController.abort()
                if (!textureTileIds!.has(textureTileId)) {
                    throw new Error("ThreeTextureTilesManager: inconsistent state - evicted tile not tracked")
                }
                textureTileIds!.delete(textureTileId)
            }
        }
        const complete = () => removeWriteRequest(false)
        const abort = () => removeWriteRequest(true)
        const downPrioritize = () => removeWriteRequest(true)

        const tileWriteInfo: TileWriteInfo = {
            textureTileId,
            priority,
            shouldLockTile,
            abortController: new AbortController(),
            complete,
            abort,
            downPrioritize,
        }

        const newTileWriteRequest: TileWriteRequest = {
            tileWriteInfo,
            lastRequestedFrame: this.currentFrame,
            updatePriority: (_newPriority) => {
                // if (this.tileWriteQueue.contains(tileWriteInfo)) {
                //     this.tileWriteQueue.updatePriority(tileWriteInfo, newPriority)
                // }
            },
        }
        this.tileWriteRequestByTextureTileIds.set(textureTileId, newTileWriteRequest)
        this.numTilesLoading++
        this.callback.onLoadingCountChanged(this.numTilesLoading)

        if (asBackgroundTask) {
            return this.callback.scheduleTask(() => this.writeTile(tileWriteInfo))
        } else {
            return this.writeTile(tileWriteInfo)
        }
    }

    private handleLegacyTileWriteRequests(): void {
        const tilesToDownPrioritize: TileWriteRequest[] = []
        this.tileWriteRequestByTextureTileIds.forEach((tileWriteRequest) => {
            if (!tileWriteRequest.tileWriteInfo.shouldLockTile && this.currentFrame - tileWriteRequest.lastRequestedFrame > this.cancelFrameThreshold) {
                tilesToDownPrioritize.push(tileWriteRequest)
            }
        })
        tilesToDownPrioritize.forEach((tileWriteRequest) => tileWriteRequest.tileWriteInfo.downPrioritize())
    }

    private async loadTileImage(request: TileRequest, priority: number, abortSignal: AbortSignal): Promise<TileResponse> {
        const promisedTextureTile = this.textureTileLoader.requestTile(request, "TileResponse", priority, abortSignal)
        // if the queue gets longer than the capacity of the atlas we expand the atlas
        this.atlasManager.setMinCapacity(request.format, Math.ceil(this.textureTileLoader.queueLength / 2)) // /2 because we load two tiles per slot (fine and coarse)
        return promisedTextureTile
    }

    private async writeTile(tileWriteInfo: TileWriteInfo): Promise<void> {
        const execute = async () => {
            const {tiledTextureId, mipLevel, tileIndex} = decodeTextureTileId(tileWriteInfo.textureTileId)
            const tiledTexture = this.getTiledTexture(tiledTextureId)
            if (tileWriteInfo.abortController.signal.aborted) {
                return
            }
            const promisedTileImage = this.loadTileImage(
                {
                    ...tiledTexture.getTileFetchParamsFine(mipLevel, tileIndex),
                    format: tiledTexture.atlasFormat,
                },
                tileWriteInfo.priority,
                tileWriteInfo.abortController.signal,
            )
            const promisedCoarserTileImage =
                mipLevel < tiledTexture.numMipLevels - 1
                    ? this.loadTileImage(
                          {
                              ...tiledTexture.getTileFetchParamsCoarse(mipLevel, tileIndex),
                              format: tiledTexture.atlasFormat,
                          },
                          tileWriteInfo.priority,
                          tileWriteInfo.abortController.signal,
                      )
                    : null
            let [tileImage, coarserTileImage] = await Promise.all([promisedTileImage, promisedCoarserTileImage])
            if (USE_DEBUG_TILE_IMAGE) {
                const debugResult = this.callback.createDebugTileImages?.(tileImage, coarserTileImage, mipLevel, tileIndex)
                if (debugResult) {
                    tileImage = debugResult.fine
                    coarserTileImage = debugResult.coarser
                }
            }
            if (!tileWriteInfo.abortController.signal.aborted) {
                const atlasTileInfo = this.atlasManager.addTile(
                    tiledTexture.atlasFormat,
                    tileWriteInfo.textureTileId,
                    tileImage,
                    coarserTileImage,
                    tileWriteInfo.shouldLockTile,
                    this.currentFrame,
                )
                tiledTexture.setTileLookup(mipLevel, tileIndex, atlasTileInfo)
                this.callback.onTileUpdated(tileWriteInfo.textureTileId)
            }
        }
        return execute()
            .then(() => {
                if (!tileWriteInfo.abortController.signal.aborted) {
                    tileWriteInfo.complete()
                }
            })
            .catch((error) => {
                if ((error as DOMException).name !== "AbortError") {
                    this.logger.error("ThreeTextureTilesManager: error processing tile writes", error)
                }
                if (!tileWriteInfo.abortController.signal.aborted) {
                    tileWriteInfo.abort()
                }
            })
    }

    private async updateCoarsestTile(tiledTexture: ThreeTiledTexture) {
        const coarsestMipLevel = tiledTexture.numMipLevels - 1
        const coarsestTileIndex = {x: 0, y: 0}
        const coarsestTileTextureTileId = encodeTextureTileId(tiledTexture.id, coarsestMipLevel, coarsestTileIndex)
        await this.requestWriteTile(coarsestTileTextureTileId, PRIORITY_INITIAL_TILE, true, false)
    }

    private onTileEvicted(textureTileId: TextureTileId): void {
        if (TRACE) {
            this.logger.trace("ThreeTextureTilesManager: tile evicted from atlas", textureTileId)
        }
        this.removeTile(textureTileId, false, true)

        // if the coarsest tile is evicted (due to an atlas reinit) we reload it right away
        const {tiledTextureId, mipLevel} = decodeTextureTileId(textureTileId)
        const tiledTexture = this.tiledTextureById.get(tiledTextureId)
        if (tiledTexture && mipLevel === tiledTexture.numMipLevels - 1) {
            // defer past the current atlas resize to avoid re-entrant setMinCapacity calls
            queueMicrotask(async () => {
                if (this.tiledTextureById.has(tiledTextureId)) {
                    await this.updateCoarsestTile(tiledTexture)
                }
            })
        }
    }

    private fetchNextFreeTiledTextureId(): number {
        const iterator = this.availableTiledTextureIds.values()
        const result = iterator.next()
        if (result.done) {
            throw new Error("ThreeTextureTilesManager: no more free IDs available")
        }
        const tiledTextureId = result.value
        this.availableTiledTextureIds.delete(tiledTextureId)
        return tiledTextureId
    }

    private get atlasManager(): ThreeTextureTileAtlasManager {
        if (!this.atlasManagerInstance) {
            const renderer = this.callback.getRenderer()
            if (!renderer) {
                throw new Error("ThreeTextureTilesManager: renderer not initialized")
            }
            this.atlasManagerInstance = new ThreeTextureTileAtlasManager(
                renderer,
                {onTileEvictedCallback: (textureTileId) => this.onTileEvicted(textureTileId)},
                this.callback.createWebgl2RgbaStaging ? (w, h) => this.callback.createWebgl2RgbaStaging!(w, h) : undefined,
            )
            this.atlasManagerInstance.setFiltering(this.textureFiltering)
            this.atlasManagerInstance.setStreamingMode(this.currentStreamingMode)
            this.atlasManagerInstance.setMaxPixelsPerTexel(this.currentMaxPixelsPerTexel)
        }
        return this.atlasManagerInstance
    }

    private readonly tiledTextureCallback: ThreeTiledTextureCallback
    private readonly availableTiledTextureIds: Set<number>
    private readonly dummyUvNode = TSL.uv()

    private readonly tiledTextureByIndexDownloadUrl = new Map<string, ThreeTiledTexture | Promise<ThreeTiledTexture>>()
    private readonly tiledTextureById = new Map<number, ThreeTiledTexture>()
    private readonly textureTileIdsByTiledTextureId = new Map<number, Set<TextureTileId>>()

    private readonly tileWriteRequestByTextureTileIds = new Map<TextureTileId, TileWriteRequest>()
    private currentFrame = 0

    private readonly dependentTextureTileIdsByTextureTileId = new Map<TextureTileId, TextureTileId[]>()

    private atlasManagerInstance?: ThreeTextureTileAtlasManager
    private textureFiltering = false
    private currentStreamingMode: ExplicitTextureStreamingMode = "high-quality"
    private currentMaxPixelsPerTexel = 1.0
    private readonly cancelFrameThreshold = 10

    private numTilesLoading = 0
    private tileUpdaterMaterialCacheInstance?: ThreeViewportTextureTileUpdaterMaterialCache = new ThreeViewportTextureTileUpdaterMaterialCache()
}
